/**
 * Disposes GPU/CPU resources held by Three.js object hierarchies so backing
 * {@link ArrayBuffer}s for {@link THREE.BufferAttribute}s can be collected.
 */

import * as THREE from 'three';

/**
 * Disposes geometry and materials attached to a single node (used by {@link disposeObject3DSubtree}).
 */
export function disposeObject3DNode(object: THREE.Object3D): void {
  if (object instanceof THREE.BatchedMesh) {
    object.dispose();
    return;
  }

  if (
    object instanceof THREE.Mesh ||
    object instanceof THREE.Line ||
    object instanceof THREE.LineSegments ||
    object instanceof THREE.LineLoop
  ) {
    object.geometry?.dispose();
  }
  if (object instanceof THREE.Points) {
    object.geometry?.dispose();
  }
  if (object instanceof THREE.Sprite) {
    object.geometry?.dispose();
  }

  if (object instanceof THREE.SkinnedMesh) {
    object.skeleton?.dispose();
  }

  if (object instanceof THREE.Light) {
    const shadow = (object as THREE.Light & { shadow?: THREE.LightShadow }).shadow;
    shadow?.map?.dispose();
  }

  const withMaterial = object as THREE.Object3D & {
    material?: THREE.Material | THREE.Material[];
  };
  const mat = withMaterial.material;
  if (mat) {
    const materials = Array.isArray(mat) ? mat : [mat];
    for (const m of materials) {
      if (m instanceof THREE.Material) {
        m.dispose();
      }
    }
  }
}

function disposeObject3DSubtreePostOrder(object: THREE.Object3D): void {
  const snapshot = object.children.slice();
  for (const child of snapshot) {
    disposeObject3DSubtreePostOrder(child);
  }
  disposeObject3DNode(object);
}

/**
 * Post-order dispose of geometries and materials under `root` (children before parents).
 */
export function disposeObject3DSubtree(root: THREE.Object3D): void {
  disposeObject3DSubtreePostOrder(root);
}
